import { queryOptions } from "@tanstack/react-query"
import type { QueryClient, UseMutationOptions } from "@tanstack/react-query"
import { fetchAuthSession } from "aws-amplify/auth"
import { MembershipMini } from "ipctg-inflow-types/InFlowDB"
import { ReserveMembershipsFailReason } from "ipctg-inflow-types/InFlowEnums"
import { ReserveApplyCodePOSTSuccessResponse } from "ipctg-inflow-types/InFlowReserveAPI"

import api from "@/utilities/api"

import { AccessCodeError, MembershipsError } from "@/utilities/expectedErrors"

import {
  CALENDARS_BASE_KEY,
  ME_BASE_KEY,
  MEMBERSHIPS_BASE_KEY,
  RESERVE_BASE_KEY,
  RESERVE_CALENDAR_AVAILABILITY_BASE_KEY,
  RESERVE_RATES_BASE_KEY,
} from "./keys"

export const membershipQueryOptions = (args: { locationID: string | null }) => {
  return queryOptions({
    /**
     * Keyed under ME_BASE_KEY so the root auth listener's
     * resetQueries([ME_BASE_KEY]) clears memberships on sign-in/out.
     */
    queryKey: [ME_BASE_KEY, MEMBERSHIPS_BASE_KEY, args.locationID],
    queryFn: async (): Promise<MembershipMini[]> => {
      const session = await fetchAuthSession()
      const cognitoIDToken = session.tokens?.idToken?.toString()
      if (!cognitoIDToken)
        throw new MembershipsError(ReserveMembershipsFailReason.Unauthorized)

      const rsp = await api.memberships.POST({
        cognitoIDToken,
        locationID: args.locationID,
      })
      if ("failReason" in rsp) throw new MembershipsError(rsp.failReason)
      return rsp.memberships
    },
    retry: false,
  })
}

/**
 * Redeems an access key, which adds a membership to the signed-in user.
 * Memberships gate calendars, availability and rates, so all four are
 * invalidated once the key lands.
 */
export const applyAccessCodeMutationOptions = (
  queryClient: QueryClient,
  locationID: string | null = null
): UseMutationOptions<ReserveApplyCodePOSTSuccessResponse, Error, string> => ({
  mutationFn: async (
    promoCode: string
  ): Promise<ReserveApplyCodePOSTSuccessResponse> => {
    const cognitoIDToken = await api.auth.getCognitoIDToken()
    const rsp = await api.reserve.applyCode.POST({
      cognitoIDToken: cognitoIDToken || "",
      promoCode,
    })
    if ("failReason" in rsp) throw new AccessCodeError(rsp.failReason)

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: membershipQueryOptions({ locationID }).queryKey,
        refetchType: "active",
      }),

      queryClient.invalidateQueries({
        queryKey: [RESERVE_BASE_KEY, CALENDARS_BASE_KEY],
        refetchType: "active",
      }),

      queryClient.invalidateQueries({
        queryKey: [RESERVE_BASE_KEY, RESERVE_CALENDAR_AVAILABILITY_BASE_KEY],
        refetchType: "active",
      }),

      queryClient.invalidateQueries({
        queryKey: [RESERVE_BASE_KEY, RESERVE_RATES_BASE_KEY],
        refetchType: "active",
      }),
    ])

    return rsp
  },
})
